import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { FaInfoCircle } from 'react-icons/fa';

import showToastNotification from '../widgets/toastify';
import CustomOverlayTrigger from '../widgets/overlay_trigger';
import * as Constants from '../../helpers/constants';
import { isClearoutPhoneClient } from '../../helpers/helper';
import { ApiHelper } from '../../helpers/apihelper';

const initialState = {
  showModal: false,
  email: '',
  ttlHours: Constants.WHITELIST_TTL.DEFAULT_HOURS.LOGIN.toString(),
  isPermanent: false,
  emailMessage: '',
  errors: {},
};

const LoginWhitelist = ({ client }) => {
  const [state, setState] = useState(initialState);
  // TTL and time-left are clearoutphone-only (cophone backend); clearout clients keep the old behaviour
  const isPhoneClient = isClearoutPhoneClient({ client });
  const messageVisibleMs = isPhoneClient ?
    Constants.WHITELIST_TTL.MESSAGE_VISIBLE_MS.PHONE : Constants.WHITELIST_TTL.MESSAGE_VISIBLE_MS.DEFAULT;
  const id = useSelector((state) => state.user.id);
  const userName = useSelector(state => state.user.name);

  const validateForm = (param) => {
    let err = {};
    if (!state.email.trim()) {
      err['email'] = 'Please enter a valid email';
    }
    // Only a temporary 'add' sends a TTL (a permanent whitelist has no expiry)
    const isTemporaryAdd = isPhoneClient && param === 'add' && !state.isPermanent;
    const ttlHours = Number(state.ttlHours);
    // the backend needs at least 1 whole second, so check the rounded value
    const isTtlValid = Math.round(ttlHours * 3600) >= 1 && ttlHours <= Constants.WHITELIST_TTL.MAX_HOURS;
    if (isTemporaryAdd && !isTtlValid) {
      err['ttl'] = `Enter hours greater than 0 and up to ${Constants.WHITELIST_TTL.MAX_HOURS}`;
    }
    if (Object.keys(err).length) {
      setState((prevState) => ({ ...prevState, errors: err }));
      setTimeout(() => {
        setState((prevState) => ({ ...prevState, errors: {} }));
      }, 2000);
      return false;
    }
    return true;
  };

  const handleSaveChanges = (param) => () => {
    if (validateForm(param)) {
      let ttl;
      if (isPhoneClient && param === 'add' && !state.isPermanent) {
        ttl = Math.round(Number(state.ttlHours) * 3600);
      }

      // Whitelist and Delist act on the permanent list when ticked; Check always reports both lists, so it needs no flag
      let perma;
      if (isPhoneClient && param !== 'check') {
        perma = state.isPermanent;
      }

      let requestBody = {
        action: param,
        user_id: id,
        crm_user_data: {
          user_id: id,
          name: userName
        },
        client_secret: Constants.CLIENT_SECRET,
        email: state.email,
        ttl,
        perma,
      };

      let body = _.pick(requestBody, ApiHelper(client, 'LOGIN_WHITELIST').API_BODY);
      ApiHelper(client, 'LOGIN_WHITELIST').API(client, body)
        .then((response) => {
          if (response.status === Constants.CO_API.FAILED) {
            showToastNotification({
              message: 'failed',
              isSuccess: false,
            });
          } else if (response.status === Constants.CO_API.SUCCESS) {
            setState(prevState => ({
              ...prevState,
              emailMessage: response.data.email?.status,
            }));
            setTimeout(() => {
              setState(prevState => ({
                ...prevState,
                emailMessage: '',
              }));
            }, messageVisibleMs);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const onEmailValueChange = (e) => {
    setState(prevState => ({ ...prevState, email: e.target.value }));
  };

  const onTtlValueChange = (e) => {
    setState(prevState => ({ ...prevState, ttlHours: e.target.value }));
  };

  const onPermanentChange = (e) => {
    setState(prevState => ({ ...prevState, isPermanent: e.target.checked }));
  };

  const handleShowModal = () => {
    setState(prevState => ({ ...prevState, showModal: true }));
  };

  const handleCloseModal = () => setState(initialState);

  return (
    <>
      <Button
        variant="outline-primary"
        onClick={handleShowModal}
      >
        Login Whitelist
      </Button>
      <Modal
        show={state.showModal}
        onHide={handleCloseModal}
      >
        <Modal.Header closeButton>
          <Modal.Title className='fs-6 fw-bold'>Login Whitelist</Modal.Title>
        </Modal.Header>
        <Modal.Body className='modal-body-bg-color'>
          <Form>
            <div className='row'>
              <Form.Group
                className='d-flex'
              >
                <div className='col-md-3'>
                  <Form.Label className='w-100 py-2'>Email:</Form.Label>
                </div>
                <div className='col-md-9'>
                  <Form.Control
                    type="email"
                    placeholder="Email"
                    onChange={onEmailValueChange}
                  />
                  {state.errors ? <p className='text-danger m-0'>{state.errors.email}</p> : null}
                  <p className='m-0'>{state.emailMessage ?? null} </p>
                </div>
              </Form.Group>
            </div>
            {isPhoneClient ? (
              <div className='row mt-2'>
                <Form.Group
                  className='d-flex'
                >
                  <div className='col-md-3' />
                  <div className='col-md-9'>
                    <div className='d-flex align-items-center'>
                      <Form.Check
                        type="checkbox"
                        id="login-whitelist-permanent"
                        label="Permanent (no expiry)"
                        checked={state.isPermanent}
                        onChange={onPermanentChange}
                      />
                      <CustomOverlayTrigger
                        placement="top"
                        content={Constants.LOGIN_WHITELIST_PERMANENT_INFO}
                      >
                        <span
                          className='ms-2 text-muted'
                          tabIndex={0}
                          aria-label="About the permanent whitelist"
                        >
                          <FaInfoCircle />
                        </span>
                      </CustomOverlayTrigger>
                    </div>
                  </div>
                </Form.Group>
              </div>
            ) : null}
            {isPhoneClient ? (
              <div className='row mt-2'>
                <Form.Group
                  className='d-flex'
                >
                  <div className='col-md-3'>
                    <Form.Label className='w-100 py-2'>TTL (hrs):</Form.Label>
                  </div>
                  <div className='col-md-9'>
                    <Form.Control
                      type="number"
                      min="0"
                      max={Constants.WHITELIST_TTL.MAX_HOURS}
                      step="any"
                      placeholder="Hours"
                      value={state.ttlHours}
                      disabled={state.isPermanent}
                      onChange={onTtlValueChange}
                    />
                    {/* Shown below the field, not as a placeholder: the field starts filled so a placeholder never shows */}
                    <Form.Text muted>
                      Default {Constants.WHITELIST_TTL.DEFAULT_HOURS.LOGIN}h, max {Constants.WHITELIST_TTL.MAX_HOURS}h ({Constants.WHITELIST_TTL.MAX_HOURS / 24}d).
                      Capped at {Constants.WHITELIST_TTL.PASSWORD_POLICY_MAX_HOURS}h if the user&apos;s password has expired.
                    </Form.Text>
                    {state.errors ? <p className='text-danger m-0'>{state.errors.ttl}</p> : null}
                  </div>
                </Form.Group>
              </div>
            ) : null}
            <Modal.Footer>
              <Button
                variant="outline-primary"
                onClick={handleSaveChanges('add')}
              >
                Whitelist
              </Button>
              <Button
                variant="outline-primary"
                onClick={handleSaveChanges('check')}
              >
                Check
              </Button>
              <Button
                variant="outline-primary"
                onClick={handleSaveChanges('remove')}
              >
                Delist
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default LoginWhitelist;
